// Belmont Rapid Infuser RI-2, condensed from Belmont's Quick Setup Guide
// (701-00218 Rev. 004) and Operator's Manual (702-00190 Rev. 008).
// Written for the clinician at the bedside; the techs usually load and prime.

export const BELMONT_SOURCES = [
  {
    title: 'Quick Setup Guide',
    href: 'https://belmontmedtech.com/index.php/system/files/docs/701-00218_004_RI2-Quick%20Setup%20Guide%20%281%29.pdf',
  },
  {
    title: "Operator's Manual",
    href: "https://belmontmedtech.com/index.php/system/files/docs/702-00190_008_Manual%20Operator%26%23039%3Bs%20RI-2%20English.pdf",
  },
  {
    title: 'Instructional video',
    href: 'https://belmontmedtech.com/belmontr-rapid-infuser-ri-2-instructional-video',
    kind: 'video',
  },
];

export const BELMONT_SUPPORT = { label: 'Technical support', phone: '1-855-397-4547' };

/** Readouts and keys on the Infuse screen. Numbers match the callouts on the schematic. */
export const SCREEN_LEGEND = [
  { n: 1, label: 'Set and actual rate', text: 'INFUSE starts at 2.5 mL/min' },
  { n: 2, label: 'Volume infused', text: 'Pauses during the air purge every 500 mL' },
  { n: 3, label: 'Output temperature', text: '37.5 °C above 50 mL/min' },
  { n: 4, label: 'Line pressure', text: 'Limit 300 mmHg. Near it the pump slows itself; SET RATE accepts the actual rate' },
  { n: 5, label: 'Rate ▲ ▼', text: '10 mL/min per press, hold to ramp' },
  { n: 6, label: '500', text: 'Straight to 500 mL/min' },
  { n: 7, label: 'BOLUS', text: 'Fixed volume, see below' },
  { n: 8, label: 'RECIRC', text: 'Warms and de-airs for 5 minutes' },
  { n: 9, label: 'STOP', text: 'Pump and heat off, display stays' },
];

export const INFUSING = [
  {
    title: 'Bolus',
    items: [
      'Hold BOLUS to cycle the volume: 100 · 200 · 400 · 500 · 1000 mL. Release to start',
      'Runs at 200 mL/min by default. The rate keys change it',
      'When it finishes, the rate returns to where it was',
    ],
  },
  {
    title: 'On battery',
    items: ['Unplugged: 50 mL/min at most, no heating, about 30 minutes'],
  },
];

export const ALARMS = [
  { title: 'Air Detection', what: 'Air in the line, or tubing unseated in the detector', fix: 'Open the door, squeeze the tubing below the detector, reseat it, REPRIME' },
  { title: 'Fluid Out', what: 'Empty bag, closed clamp, unseated or stretched line, clogged filter', fix: 'Add fluid or open clamps, reseat the line, REPRIME. Reservoir stays empty: replace it' },
  { title: 'High Pressure', what: 'Blocked line, cannula too small, poor IV site', fix: 'Check the path for kinks, upsize the cannula, NEXT' },
  { title: 'Door Open', what: 'Door opened while pumping', fix: 'Close it' },
  { title: 'Missing Disposable', what: 'No set loaded', fix: 'Load it, close the door, NEXT' },
  { title: 'Error #101', what: 'Heater fault: wet or dirty windows on the set or probes, or started without AC', fix: 'Wipe and dry the windows and probes, RETRY. Confirm AC POWER PRESENT' },
  { title: 'Error #102', what: 'Fluid over temperature', fix: 'Do not infuse the blood in the set. Clamp, remove the set, restart with a new one', danger: true },
  { title: 'Error #201–210', what: 'Hardware fault', fix: 'Do what the screen says, usually power off and restart. #207: reseat the pump tubing. Persists: out of service, call Belmont' },
  { title: 'Low Battery', what: 'Running unplugged', fix: 'Plug it in' },
];

export const QUIRKS = [
  { problem: 'Rate keeps falling below set', fix: 'Pressure control. Kink, cannula, or raise the limit' },
  { problem: 'Not warming', fix: 'Wet or dirty windows on the set or the sensor. Wipe with alcohol' },
  { problem: 'Pump is loud', fix: 'Tubing misloaded or roller hitting the door. Open and reseat' },
];

export const FLUIDS = {
  yes: ['Anticoagulated blood, RBCs, FFP', 'Washed cell saver blood', 'Saline, Plasma-Lyte A, Normosol', 'Albumin 5%', 'Hetastarch in saline'],
  no: ['Platelets, cryo, granulocytes', 'Anything with dextrose', '3% or 0.45% saline', 'Bicarbonate', 'Albumin above 5%', 'Lipids, amino acids, dextran', 'Drugs'],
  calcium: 'Calcium-containing fluids (LR, Hextend) clot citrated blood in the set. Only through a disposable that has never held blood.',
};

export const SETUP_NOTE = 'The anesthesia techs usually load and prime. THI uses the 3 L reservoir, not the chamber in the set.';

export const SETUP = [
  { title: 'Power', steps: ['Dedicated 20 A outlet, own breaker, nothing else on it', 'Seat the C-19 connector, breaker on, confirm AC POWER PRESENT'] },
  {
    title: '3 L reservoir',
    steps: [
      'Unclip the small reservoir chamber from the set: press the lock tab on the pump tubing, unscrew the luer on the thin recirc line',
      'Holder on the IV pole, reservoir in the holder',
      'Three fluid tails onto the top of the reservoir, then connect the reservoir to the set',
      'Adjust the holder so the two leads underneath are slack. Stretched or kinked leads cause Fluid Out alarms',
    ],
  },
  {
    title: 'Load the set',
    steps: [
      'Heat exchanger in, red arrow up',
      'Pressure chamber into its well, wide infuse line into the air detector',
      'Infuse line left of the valve wand, thin recirc line right',
      'Interlock block on the shelf, blue arrow inward',
      'Blue pump tubing in the left groove, small tubing right, close and latch',
    ],
  },
  {
    title: 'Prime',
    steps: [
      'Spike bags by the finger grip, open clamps. Saline or Plasma-Lyte, never blood',
      'PRIME: 100 mL at 500 mL/min, stops on its own',
      'Uncap the patient line. PT. LINE PRIME, press for 50 or hold for 200 mL/min, until no air',
      'STOP, inspect the line past the valve wand, connect',
    ],
  },
];
